import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Bookmark, Share2, Play, ExternalLink, Coins, Star, Download, ChevronUp, ChevronDown, ShieldCheck, MessageCircle, Flag, Sparkles, Compass, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { fetchFeed, fetchSmartFeed, likeApp, saveApp, trackEvent, formatDownloads } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import TipModal from "@/components/TipModal";
import FollowButton from "@/components/FollowButton";
import ShareCardModal from "@/components/ShareCardModal";
import PwaLauncher from "@/components/PwaLauncher";
import { FEED, FEED_TABS, SHARE } from "@/constants/testIds";

export default function Feed() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState(user ? "smart" : "discover"); // smart | discover
  const [items, setItems] = useState([]);
  const [tipTarget, setTipTarget] = useState(null);
  const [shareTarget, setShareTarget] = useState(null);
  const [launchTarget, setLaunchTarget] = useState(null);
  const [likedMap, setLikedMap] = useState({});
  const [savedMap, setSavedMap] = useState({});
  const containerRef = useRef(null);
  const loadingRef = useRef(false);
  const dwellRef = useRef({}); // slug -> {enterAt}

  const loadMore = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    try {
      const fn = mode === "smart" ? fetchSmartFeed : fetchFeed;
      const { apps } = await fn({ limit: 6 });
      setItems((prev) => {
        const seen = new Set(prev.map((a) => a.slug));
        const next = [...prev];
        apps.forEach((a) => { if (!seen.has(a.slug)) next.push(a); });
        return next;
      });
    } finally {
      loadingRef.current = false;
    }
  }, [mode]);

  // reset feed on mode change
  useEffect(() => {
    setItems([]);
    loadingRef.current = false;
    loadMore();
  }, [mode, loadMore]);

  // infinite scroll + dwell tracking via IntersectionObserver
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => {
      if (el.scrollTop + el.clientHeight > el.scrollHeight - 800) loadMore();
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [loadMore]);

  const scrollBy = (dir) => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollBy({ top: dir * el.clientHeight, behavior: "smooth" });
  };

  // keyboard
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowDown" || e.key === "j") scrollBy(1);
      if (e.key === "ArrowUp" || e.key === "k") scrollBy(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Track dwell using IntersectionObserver on each card
  const registerCard = useCallback((slug, node) => {
    if (!node) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && e.intersectionRatio > 0.75) {
          dwellRef.current[slug] = { enterAt: Date.now() };
          trackEvent(slug, "view");
        } else if (dwellRef.current[slug]?.enterAt) {
          const secs = (Date.now() - dwellRef.current[slug].enterAt) / 1000;
          if (secs > 1) trackEvent(slug, "dwell", secs);
          delete dwellRef.current[slug];
        }
      });
    }, { threshold: [0, 0.75, 1] });
    io.observe(node);
    return () => io.disconnect();
  }, []);

  const toggleLike = async (app) => {
    if (!user) { nav("/login"); return; }
    try {
      const r = await likeApp(app.slug);
      setLikedMap((m) => ({ ...m, [app.slug]: r.liked }));
      if (r.liked) toast.success("Liked");
    } catch { toast.error("Try again"); }
  };
  const toggleSave = async (app) => {
    if (!user) { nav("/login"); return; }
    try {
      const r = await saveApp(app.slug);
      setSavedMap((m) => ({ ...m, [app.slug]: r.saved }));
      toast.success(r.saved ? "Saved to library" : "Removed");
    } catch { toast.error("Try again"); }
  };
  const share = (app) => {
    const url = `${window.location.origin}/app/${app.slug}`;
    if (navigator.share) navigator.share({ title: app.name, url }).catch(() => {});
    else { navigator.clipboard.writeText(url); toast.success("Link copied"); }
  };
  const openApp = (app) => {
    trackEvent(app.slug, "open");
    if (app.is_pwa && app.launch_url) {
      setLaunchTarget(app);
      toast.success(`Launching ${app.name}…`, { description: "Playing right here in Jixxeo" });
    } else {
      nav(`/app/${app.slug}`);
    }
  };

  return (
    <div data-testid={FEED.root} className="fixed inset-x-0 top-16 bottom-0 overflow-hidden bg-[#050505]">
      {/* Feed tabs */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30">
        <div className="glass-strong rounded-full p-1 flex gap-1 border border-white/10">
          <TabButton active={mode === "smart"} onClick={() => setMode("smart")}
            testId={FEED_TABS.forYou} icon={<Sparkles className="w-3.5 h-3.5" />}>For You</TabButton>
          <TabButton active={mode === "discover"} onClick={() => setMode("discover")}
            testId={FEED_TABS.discover} icon={<Compass className="w-3.5 h-3.5" />}>Discover</TabButton>
        </div>
      </div>

      <div ref={containerRef} className="h-full overflow-y-scroll snap-y snap-mandatory hide-scrollbar"
           style={{ scrollBehavior: "smooth" }}>
        {items.map((app) => (
          <FeedCard
            key={app.slug}
            app={app}
            liked={likedMap[app.slug]}
            saved={savedMap[app.slug]}
            onLike={() => toggleLike(app)}
            onSave={() => toggleSave(app)}
            onTip={() => user ? setTipTarget(app) : nav("/login")}
            onShare={() => setShareTarget(app)}
            onOpen={() => openApp(app)}
            registerCard={registerCard}
          />
        ))}
        {items.length === 0 && (
          <div className="h-full flex items-center justify-center mono text-[#00F0FF] animate-pulse">Loading feed…</div>
        )}
      </div>

      <div className="hidden md:flex flex-col gap-2 absolute right-6 top-1/2 -translate-y-1/2 z-30">
        <button onClick={() => scrollBy(-1)} className="w-10 h-10 rounded-full glass-strong flex items-center justify-center hover:text-[#00F0FF]"><ChevronUp /></button>
        <button onClick={() => scrollBy(1)} className="w-10 h-10 rounded-full glass-strong flex items-center justify-center hover:text-[#00F0FF]"><ChevronDown /></button>
      </div>

      {tipTarget && <TipModal open={!!tipTarget} onClose={() => setTipTarget(null)} app={tipTarget} />}
      {shareTarget && <ShareCardModal open={!!shareTarget} onClose={() => setShareTarget(null)} app={shareTarget} />}
      {launchTarget && <PwaLauncher open={!!launchTarget} onClose={() => setLaunchTarget(null)} app={launchTarget} />}
    </div>
  );
}

function TabButton({ active, onClick, children, icon, testId }) {
  return (
    <button data-testid={testId} onClick={onClick}
      className="mono text-[10px] uppercase tracking-widest px-4 py-2 rounded-full transition-colors flex items-center gap-1.5"
      style={{
        background: active ? "#00F0FF" : "transparent",
        color: active ? "#000" : "rgba(255,255,255,0.7)",
      }}>
      {icon} {children}
    </button>
  );
}

function FeedCard({ app, liked, saved, onLike, onSave, onTip, onShare, onOpen, registerCard }) {
  const nodeRef = useRef(null);
  useEffect(() => {
    return registerCard(app.slug, nodeRef.current);
  }, [app.slug, registerCard]);

  return (
    <section ref={nodeRef} data-testid={FEED.card} className="snap-start h-full w-full relative flex items-stretch">
      <div className="absolute inset-0">
        <img src={app.screenshots?.[0] || app.banner_url} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-[#050505]/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/70 via-transparent to-[#050505]/30" />
      </div>

      <div className="relative z-10 flex flex-col md:flex-row items-stretch w-full max-w-7xl mx-auto px-4 md:px-10 py-6 gap-6">
        <div className="flex-1 flex flex-col justify-end pb-6 md:pb-16 max-w-2xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF] mb-2 flex items-center gap-2">
              {app.is_pwa && <span className="bg-[#39FF14]/20 text-[#39FF14] px-2 py-0.5 rounded-full border border-[#39FF14]/40">PWA · Instant</span>}
              <span>{app.category}</span>
            </div>
            <div className="flex items-center gap-4 mb-3">
              <img src={app.icon_url} alt="" className="w-16 h-16 md:w-20 md:h-20 rounded-2xl ring-2 ring-white/20" />
              <div className="min-w-0">
                <h2 className="font-heading font-black text-3xl md:text-5xl uppercase tracking-tighter leading-none">{app.name}</h2>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="text-white/80 text-sm">{app.developer}</span>
                  {app.developer_verified && <ShieldCheck className="w-3.5 h-3.5 text-[#00F0FF]" />}
                  <FollowButton developerSlug={app.developer_slug} developerName={app.developer} size="sm" />
                </div>
              </div>
            </div>
            <p className="text-white/80 text-base md:text-lg max-w-xl">{app.tagline}</p>
            <div className="flex items-center gap-4 mt-3 mono text-xs text-white/70 flex-wrap">
              <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5 fill-[#FFE300] text-[#FFE300]" /> {app.rating.toFixed(1)}</span>
              <span className="flex items-center gap-1" title="Launches"><Play className="w-3.5 h-3.5" /> {formatDownloads(app.downloads)} launches</span>
              <span>{app.file_size_mb} MB</span>
              {!app.is_free && <span className="text-[#FFE300]">${app.price}</span>}
              {app.is_open_source && <span className="text-[#00F0FF]">Open Source</span>}
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
              {app.tags?.slice(0, 4).map((t) => (
                <span key={t} className="mono text-[10px] uppercase bg-white/5 border border-white/10 rounded-full px-2.5 py-1 text-white/70">#{t.toLowerCase()}</span>
              ))}
            </div>

            <div className="flex items-center gap-3 mt-6">
              <button data-testid={FEED.openBtn} onClick={onOpen}
                className="inline-flex items-center gap-2 bg-[#00F0FF] hover:bg-white text-black font-bold mono text-xs uppercase tracking-widest px-6 py-3 rounded-full transition-colors pulse-neon">
                <Play className="w-4 h-4" /> {app.is_pwa ? "Launch Instant" : "Open Details"}
                {app.is_pwa && <ExternalLink className="w-3 h-3" />}
              </button>
              <Link data-testid={FEED.detailsBtn} to={`/app/${app.slug}`} className="mono text-[10px] uppercase tracking-widest text-white/70 hover:text-[#00F0FF] px-4 py-3">
                See details →
              </Link>
            </div>
          </motion.div>
        </div>

        <div className="absolute right-4 md:right-16 bottom-24 md:bottom-24 z-20 flex flex-col gap-4 items-center">
          <RailBtn onClick={onLike} icon={<Heart className={`w-6 h-6 ${liked ? "fill-[#FF003C] text-[#FF003C]" : ""}`} />} label={formatDownloads(app.likes || 0)} testId={FEED.likeBtn} />
          <RailBtn onClick={onSave} icon={<Bookmark className={`w-6 h-6 ${saved ? "fill-[#39FF14] text-[#39FF14]" : ""}`} />} label={saved ? "Saved" : "Save"} testId={FEED.saveBtn} />
          <RailBtn onClick={onTip} icon={<Coins className="w-6 h-6 text-[#FFE300]" />} label={formatDownloads(app.tips_received || 0)} testId={FEED.tipBtn} accent="#FFE300" />
          <RailBtn onClick={onShare} icon={<ImageIcon className="w-6 h-6" />} label="Share" testId={SHARE.cardBtn} />
          <Link to={`/app/${app.slug}#comments`} className="group flex flex-col items-center gap-1">
            <span className="w-12 h-12 rounded-full glass-strong flex items-center justify-center border border-white/10 group-hover:border-white/30 text-white">
              <MessageCircle className="w-6 h-6" />
            </span>
            <span className="mono text-[9px] uppercase tracking-widest text-white/70">
              {formatDownloads(app.comments_count || 0)}
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
}

function RailBtn({ icon, label, onClick, testId, accent = "#ffffff" }) {
  return (
    <button data-testid={testId} onClick={onClick} className="group flex flex-col items-center gap-1">
      <span className="w-12 h-12 rounded-full glass-strong flex items-center justify-center border border-white/10 group-hover:border-white/30 group-active:scale-95 transition-transform text-white">
        {icon}
      </span>
      <span className="mono text-[9px] uppercase tracking-widest" style={{ color: label === "Save" || label === "Share" ? undefined : accent }}>
        {label}
      </span>
    </button>
  );
}
